import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, Drawer, FormControlLabel, IconButton, InputAdornment, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import SearchIcon from '@material-ui/icons/Search';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { exportAuditCsv, fetchAuditClinics, fetchAuditRunStatus, startFullAudit } from '../actions/audit';
import { updateMember, deleteMember, FETCH_MEMBERS_SUCCESS } from '../actions/member';
import { FETCH_CUSTOMERS_SUCCESS } from '../actions/customer';
import { updateLicense, FETCH_LICENSES_SUCCESS } from '../actions/license';
import { MemberActionContext } from '../containers/MemberList';
import UpdateMember from './modal/UpdateMember';
import CheckForIssues, { AuditSnackbar } from './CheckForIssues';

const auditIssueLabels = {
    configuration: 'Configuration',
    authentication: 'Authentication',
    access_denied: 'Access denied',
    connection: 'API connection',
    timeout: 'Request timeout',
    invalid_response: 'Invalid API response',
    appointment_configuration: 'Appointment setup',
    no_availability: 'No availability',
    rate_limit: 'Rate limited',
    provider_error: 'Provider error'
};
const getAuditIssueLabels = types => [...new Set((Array.isArray(types) && types.length ? types : ['unknown'])
    .map(type => auditIssueLabels[type] || 'Uncategorized issue'))];

const ManageAudit = () => {
    const dispatch = useDispatch();
    const [editingId, setEditingId] = useState(null);
    const [editorMember, setEditorMember] = useState(null);
    const [editorError, setEditorError] = useState('');
    const [editorAttempt, setEditorAttempt] = useState(0);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [startingAudit, setStartingAudit] = useState(false);
    const [auditRun, setAuditRun] = useState(null);
    const [auditStatusError, setAuditStatusError] = useState('');
    const observedRun = useRef(null);
    const statusGeneration = useRef(0);
    const auditRunning = Boolean(auditRun && auditRun.status === 'running');
    const [exporting, setExporting] = useState({});
    const [orderBy, setOrderBy] = useState('has_issues');
    const [order, setOrder] = useState('desc');
    const [showHealthyClinics, setShowHealthyClinics] = useState(true);
    const [search, setSearch] = useState('');
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try { setMembers(await fetchAuditClinics()); }
        catch (_) { setError('Could not load clinics. Please try again.'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!editingId) return;
        let cancelled = false;
        setEditorMember(null);
        setEditorError('');
        const loadEditor = async () => {
            try {
                const [memberResponse, customerResponse, licenseResponse] = await Promise.all(
                    ['members', 'customers', 'licenses'].map(resource => axios.get(process.env.REACT_APP_ENDPOINT_URL + '/v1/' + resource))
                );
                if (cancelled) return;
                const fullMember = memberResponse.data.members.find(member => member._id === editingId);
                if (!fullMember) throw new Error('Clinic no longer exists.');
                const customers = customerResponse.data.customers;
                const licenses = licenseResponse.data.licenses;
                dispatch({ type: FETCH_MEMBERS_SUCCESS, members: memberResponse.data.members, receivedAt: Date.now() });
                dispatch({ type: FETCH_CUSTOMERS_SUCCESS, customers, receivedAt: Date.now() });
                dispatch({ type: FETCH_LICENSES_SUCCESS, licenses, receivedAt: Date.now() });
                setEditorMember({ ...fullMember,
                    customer: customers.find(customer => customer._id === fullMember.customer_id),
                    license: licenses.find(license => license._id === fullMember.license_id) });
            } catch (_) {
                if (!cancelled) setEditorError('Could not load clinic details. Please try again.');
            }
        };
        loadEditor();
        return () => { cancelled = true; };
    }, [editingId, editorAttempt, dispatch]);
    const runFullAudit = async () => {
        if (startingAudit || auditRunning || !auditRun || auditStatusError) return;
        setStartingAudit(true);
        statusGeneration.current++;
        try {
            const run = await startFullAudit();
            observedRun.current = run;
            setAuditRun(run);
            setResult({ has_issues: false, message: 'Full audit started.' });
        } catch (failure) {
            setResult({ has_issues: true, message: (failure.response && failure.response.data && failure.response.data.message)
                || 'Could not start the full audit. Please try again.' });
        } finally { setStartingAudit(false); }
    };
    useEffect(() => {
        let cancelled = false;
        let timer;
        const poll = async () => {
            const generation = statusGeneration.current;
            try {
                const run = await fetchAuditRunStatus();
                if (cancelled || generation !== statusGeneration.current) return;
                if (!run || !['idle', 'running', 'completed', 'failed'].includes(run.status)) throw new Error('Invalid audit status');
                const previous = observedRun.current;
                observedRun.current = run;
                setAuditRun(run);
                setAuditStatusError('');
                if (previous && previous.status === 'running' && (run.status !== 'running' || previous.run_id !== run.run_id)) {
                    load();
                    if (run.status === 'completed' || run.status === 'failed') {
                        setResult({ has_issues: run.status === 'failed', message: run.status === 'completed'
                            ? 'Audit completed.' : (run.error || 'The audit did not complete. Please try again.') });
                    }
                }
            } catch (_) {
                if (!cancelled && generation === statusGeneration.current) setAuditStatusError('Could not refresh audit status. Retrying automatically.');
            } finally {
                if (!cancelled) timer = setTimeout(poll, 5000);
            }
        };
        poll();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [load]);
    const exportCsv = async report => {
        if (exporting[report]) return;
        setExporting(previous => ({ ...previous, [report]: true }));
        try { await exportAuditCsv(report); }
        catch (_) { setResult({ has_issues: true, message: 'Could not export the '
            + (report === 'issues' ? 'issues' : 'upcoming slots') + ' CSV. Please try again.' }); }
        finally { setExporting(previous => ({ ...previous, [report]: false })); }
    };
    const onResult = value => {
        setResult(value);
        if (value._id) {
            setMembers(previous => previous.map(member => member._id === value._id ? { ...member, ...value } : member));
        }
    };
    const columns = [
        ['clinic_id', 'Clinic ID'], ['account_number', 'Account number'], ['clinic_name', 'Clinic'], ['has_issues', 'Issues'],
        ['next_available_slot_date', 'Next available slot'], ['audit_checked_at', 'Last checked']
    ];
    const sortBy = field => {
        setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(field);
    };
    const query = search.trim().toLowerCase();
    const sortedMembers = members.filter(member => (showHealthyClinics || member.has_issues)
        && (!query || [member.clinic_id, member.account_number, member.clinic_name,
            ...(member.has_issues ? [member.issue_description, ...getAuditIssueLabels(member.audit_issue_types)] : ['No issues'])]
            .some(value => String(value || '').toLowerCase().includes(query)))).sort((a, b) => {
        let comparison;
        if (orderBy === 'audit_checked_at' || orderBy === 'next_available_slot_date') {
            const left = a[orderBy] ? moment(a[orderBy]).valueOf() : NaN;
            const right = b[orderBy] ? moment(b[orderBy]).valueOf() : NaN;
            // Missing dates stay at the bottom in either direction.
            if (Number.isNaN(left) !== Number.isNaN(right)) return Number.isNaN(left) ? 1 : -1;
            comparison = Number.isNaN(left) ? 0 : left - right;
        } else if (orderBy === 'has_issues') {
            comparison = Number(Boolean(a.has_issues)) - Number(Boolean(b.has_issues));
        } else {
            comparison = String(a[orderBy] || '').localeCompare(String(b[orderBy] || ''), undefined, { numeric: true, sensitivity: 'base' });
        }
        return (order === 'asc' ? comparison : -comparison)
            || String(a.clinic_name || '').localeCompare(String(b.clinic_name || ''));
    });
    const progress = auditRun && auditRun.progress;
    const totalKnown = progress && typeof progress.total === 'number';
    const progressPercent = totalKnown && progress.total > 0 ? Math.min(100, Math.round(progress.processed / progress.total * 100))
        : auditRun && auditRun.status === 'completed' ? 100 : 0;
    const auditScope = auditRun && auditRun.scope === 'issues' ? 'Issue recheck' : 'Full audit';
    const currentClinic = progress && progress.current_clinic;
    return (
        <section className="members-table audit-table" aria-label="Clinic audit directory">
            <div className="audit-table__toolbar member-row-actions">
            <Button size="small" variant="contained" color="primary" disableElevation startIcon={<PlaylistAddCheckIcon />}
                onClick={runFullAudit} disabled={startingAudit || auditRunning || !auditRun || Boolean(auditStatusError)}>{startingAudit || auditRunning ? 'Running audit…' : 'Run full audit'}</Button>
            <Tooltip title="Refresh audit results"><span><IconButton size="small" color="primary" aria-label="Refresh" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
            <Button size="small" variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={() => exportCsv('issues')} disabled={exporting.issues}>
                {exporting.issues ? 'Exporting issues…' : 'Export issues CSV'}
            </Button>
            <Button size="small" variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={() => exportCsv('availability')} disabled={exporting.availability}>
                {exporting.availability ? 'Exporting upcoming slots…' : 'Export upcoming slots CSV'}
            </Button>
            <FormControlLabel label="Show clinics without issues"
                control={<Checkbox size="small" color="primary" checked={showHealthyClinics}
                    onChange={event => setShowHealthyClinics(event.target.checked)} />} />
            </div>
            <section className="audit-run" aria-label="Audit status">
                <div className="audit-run__heading">
                    <h2>{auditStatusError ? 'Audit status unavailable' : !auditRun ? 'Checking audit status…'
                        : auditRunning ? auditScope + ' running' : auditRun.status === 'idle' ? 'No audit running'
                            : 'Last ' + auditScope.toLowerCase() + (auditRun.status === 'failed' ? ' failed' : ' completed')}</h2>
                    {auditRun && auditRun.started_at && <span className="audit-run__time">
                        {auditRun.completed_at ? 'Finished ' + moment(auditRun.completed_at).format('MMM D, h:mm A')
                            : 'Started ' + moment(auditRun.started_at).format('MMM D, h:mm A')}
                    </span>}
                </div>
                {(auditRunning || (progress && auditRun.status !== 'idle')) && <>
                    <div className="audit-run__details" role="status">
                        <span>{auditStatusError ? 'Last known progress: ' : ''}{totalKnown
                            ? progress.processed + ' of ' + progress.total + ' clinics processed (' + progressPercent + '%)'
                            : 'Preparing clinic list…'}</span>
                        {currentClinic && <span className="audit-run__clinic">{auditStatusError ? 'Last reported clinic: ' : 'Checking '}
                            {currentClinic.clinic_name || currentClinic.clinic_id || currentClinic._id}
                            {currentClinic.clinic_name && currentClinic.clinic_id ? ' (' + currentClinic.clinic_id + ')' : ''}</span>}
                    </div>
                    <LinearProgress aria-label="Audit progress" variant={totalKnown ? 'determinate' : 'indeterminate'} value={progressPercent} />
                </>}
                {auditRun && auditRun.summary && <div className="audit-run__counts">
                    <span>{auditRun.summary.checked} checked</span><span>{auditRun.summary.healthy} healthy</span>
                    <span>{auditRun.summary.with_issues} with issues</span><span>{auditRun.summary.skipped} skipped</span>
                    <span className={auditRun.summary.failed ? 'audit-run__failure' : undefined}>{auditRun.summary.failed} failed</span>
                </div>}
                {auditRun && auditRun.error && <Typography color="error" role="alert">{auditRun.error}</Typography>}
                {auditStatusError && <Typography color="error" role="alert">{auditStatusError}</Typography>}
            </section>
            {loading && <CircularProgress size={24} aria-label="Loading audit results" />}
            {error && <Typography color="error" role="alert">{error}</Typography>}
            <TextField label="Search clinics" variant="outlined" size="small" margin="normal" fullWidth type="search"
                value={search} onChange={event => setSearch(event.target.value)}
                inputProps={{ 'aria-label': 'Search clinics' }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    endAdornment: search ? <InputAdornment position="end">
                        <Tooltip title="Clear search"><IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                            <CloseIcon fontSize="small" />
                        </IconButton></Tooltip>
                    </InputAdornment> : null
                }} />
            <div className="members-table__summary">
                <span>{sortedMembers.length} {sortedMembers.length === 1 ? 'clinic' : 'clinics'}</span>
                <span>{members.filter(member => member.has_issues).length} with issues</span>
            </div>
            <TableContainer className="members-table__scroll">
                <Table size="small" className="members-table__grid" aria-label="Clinic audits">
                    <TableHead><TableRow>
                        {columns.map(([field, title]) => <TableCell key={field} sortDirection={orderBy === field ? order : false}>
                            <TableSortLabel active={orderBy === field} direction={orderBy === field ? order : 'asc'}
                                onClick={() => sortBy(field)}>{title}</TableSortLabel>
                        </TableCell>)}
                        <TableCell align="right">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {sortedMembers.map(member => <TableRow hover key={member._id}
                            className={'members-table__row' + (member.has_issues ? ' members-table__row--incomplete' : '')}>
                            <TableCell className="members-table__identity"><span className="members-table__primary members-table__id">{member.clinic_id}</span></TableCell>
                            <TableCell><span className="members-table__primary">{member.account_number}</span></TableCell>
                            <TableCell className="members-table__clinic"><span className="members-table__primary">{member.clinic_name}</span></TableCell>
                            <TableCell className="audit-table__issues">{member.has_issues ? <>
                                <div className="audit-table__issue-labels">{getAuditIssueLabels(member.audit_issue_types).map(label =>
                                    <span key={label} className="members-table__status audit-table__status--issue">{label}</span>)}</div>
                                <span className="members-table__secondary">{member.issue_description || 'Issue detected'}</span>
                            </> : <em className="members-table__status members-table__status--active">No issues.</em>}</TableCell>
                            <TableCell><span className="members-table__secondary">{member.next_available_slot_date ? moment(member.next_available_slot_date).format('MMM D, YYYY') : '—'}</span></TableCell>
                            <TableCell><span className="members-table__secondary">{member.audit_checked_at ? moment(member.audit_checked_at).format('MMM D, YYYY h:mm A') : 'Never'}</span></TableCell>
                            <TableCell align="right"><div className="audit-table__row-actions member-row-actions">
                                <Button size="small" variant="outlined" color="primary" startIcon={<EditIcon fontSize="small" />}
                                    onClick={() => { setEditorMember(null); setEditorError(''); setEditingId(member._id); }}>Edit</Button>
                                <CheckForIssues member={member} onResult={onResult} />
                            </div></TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>
            {!loading && !error && sortedMembers.length === 0 && <div className="members-table__empty" role="status">
                {query ? 'No clinics match your search.' : showHealthyClinics ? 'No clinics found.' : 'No clinics with issues found.'}
            </div>}
            <Drawer anchor="right" open={Boolean(editingId)} onClose={() => setEditingId(null)}
                PaperProps={{ className: 'member-editor-drawer', role: 'dialog', 'aria-label': 'Edit member' }}>
                {editingId && (editorMember ? <MemberActionContext.Provider value={{
                    onUpdateMember: member => dispatch(updateMember(member)).then(load),
                    onDeleteMember: id => dispatch(deleteMember(id)).then(load),
                    onUpdateLicense: license => dispatch(updateLicense(license))
                }}>
                    <UpdateMember member={editorMember} onClose={() => setEditingId(null)} onRevert={load} />
                </MemberActionContext.Provider> : <div className="member-editor">
                    <header className="member-editor__header"><h2>Edit member</h2>
                        <Tooltip title="Close editor"><IconButton size="small" aria-label="Close editor" onClick={() => setEditingId(null)}><CloseIcon /></IconButton></Tooltip>
                    </header>
                    <div className="member-editor__body">{editorError ? <>
                        <Typography color="error" role="alert">{editorError}</Typography>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={() => setEditorAttempt(previous => previous + 1)}>Retry</Button>
                    </> : <CircularProgress size={24} aria-label="Loading clinic details" />}</div>
                </div>)}
            </Drawer>
            <AuditSnackbar result={result} onClose={() => setResult(null)} />
        </section>
    );
};

export default ManageAudit;
